import React, { useEffect, useState, useCallback } from "react";
import axios, { API_BASE_URL } from "../api/axios";
import ImageLoader from "../common/ImageLoader";
import "./Gallery.css";

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [modalIndex, setModalIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  const BASE_URL = API_BASE_URL;

  const closeModal = useCallback(() => setModalIndex(null), []);
  const showPrev = useCallback(() => {
    setModalIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);
  const showNext = useCallback(() => {
    setModalIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  const openModal = (index) => setModalIndex(index);

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/gallery");
      setImages(res.data || []);
    } catch (err) {
      console.error("Error fetching gallery:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (modalIndex === null) return;
      if (e.key === "ArrowLeft") showPrev();
      else if (e.key === "ArrowRight") showNext();
      else if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [modalIndex, showPrev, showNext, closeModal]);

  if (loading) {
    return (
      <div className="gallery-container loading-page">
        <div className="loading-spinner"></div>
        <p>Loading chapter memories...</p>
      </div>
    );
  }

  const currentImage = modalIndex !== null ? images[modalIndex] : null;

  return (
    <section id="gallery" className="gallery-container">
      <div className="gallery-inner">
        {/* Header */}
        <div className="section-header">
          <span className="section-tag">✦ CAPTURED MOMENTS</span>
          <h1 className="section-main-title">Chapter Gallery</h1>
          <p className="section-description">
            A visual journey through the workshops, hackathons, and memorable
            events organized by the DIET ACM Student Chapter.
          </p>
        </div>

        {images.length > 0 ? (
          <div className="gallery-grid">
            {images.map((img, index) => (
              <div
                key={img.id || index}
                className="gallery-item glass-panel"
                onClick={() => openModal(index)}
              >
                <ImageLoader
                  src={`${BASE_URL}/${img.imagePath.replace(/\\/g, "/")}`}
                  alt={`Memory ${index + 1}`}
                  className="gallery-image"
                  loading="lazy"
                />
                <div className="gallery-overlay">
                  <div className="gallery-zoom-icon">
                    <i className="fas fa-expand-alt"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-images glass-panel">
            <i className="fas fa-images no-images-icon"></i>
            <h3>No Photos Uploaded Yet</h3>
            <p>New gallery memories will be uploaded here soon.</p>
          </div>
        )}
      </div>

      {/* Modern Lightbox Modal */}
      {modalIndex !== null && currentImage && (
        <div className="modal" onClick={closeModal}>
          <button className="close" onClick={closeModal} aria-label="Close Lightbox">
            &times;
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <ImageLoader
              src={`${BASE_URL}/${currentImage.imagePath.replace(/\\/g, "/")}`}
              alt={`Event ${modalIndex + 1}`}
              className="modal-image"
              loading="eager"
            />
            <div className="modal-counter">
              {modalIndex + 1} / {images.length}
            </div>
            <button
              className="arrow left-arrow"
              onClick={showPrev}
              aria-label="Previous Image"
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              className="arrow right-arrow"
              onClick={showNext}
              aria-label="Next Image"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;